import {
  createMerchPageQuery,
  mapToMerchPageContent,
} from 'app/queries/globals/merch'
import {
  createUserCollectiblesQuery,
  mapToUserCollectibles,
} from 'app/queries/collectibles/user-collectibles'

import useQuery from './use-query'

const merchPageQuery = createMerchPageQuery()
const userCollectiblesQuery = createUserCollectiblesQuery()
const QUARTER_ORDER = ['Q1', 'Q2', 'Q3', 'Q4']

const resolveRoadmapId = (roadmap: unknown): string | undefined => {
  if (typeof roadmap === 'string') return roadmap
  if (roadmap && typeof roadmap === 'object' && 'id' in roadmap) {
    const id = (roadmap as { id?: unknown }).id
    return typeof id === 'string' ? id : undefined
  }

  return undefined
}

const resolveRoadmapYear = (roadmap: unknown): number | undefined => {
  if (roadmap && typeof roadmap === 'object' && 'year' in roadmap) {
    const year = (roadmap as { year?: unknown }).year
    return typeof year === 'number' ? year : undefined
  }

  return undefined
}

const resolveRoadmapQuarterCollectibleIds = (roadmap: unknown): string[] => {
  if (!roadmap || typeof roadmap !== 'object' || !('quarters' in roadmap)) {
    return []
  }

  const quarters = (roadmap as { quarters?: unknown }).quarters
  if (!Array.isArray(quarters)) return []

  return quarters
    .slice()
    .sort((a, b) => {
      const aQuarter =
        typeof (a as { quarter?: unknown })?.quarter === 'string'
          ? (a as { quarter?: string }).quarter?.toUpperCase() || ''
          : ''
      const bQuarter =
        typeof (b as { quarter?: unknown })?.quarter === 'string'
          ? (b as { quarter?: string }).quarter?.toUpperCase() || ''
          : ''
      const aIndex = QUARTER_ORDER.findIndex((label) => aQuarter.includes(label))
      const bIndex = QUARTER_ORDER.findIndex((label) => bQuarter.includes(label))
      const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex
      const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex
      return normalizedA - normalizedB
    })
    .map((entry) => {
      const collectible = (entry as { collectible?: { id?: unknown } | null })
        ?.collectible
      return typeof collectible?.id === 'string' ? collectible.id : undefined
    })
    .filter((id): id is string => typeof id === 'string')
}

const resolveYearFromUserCollectibles = (collectibles?: any[]): number | undefined => {
  if (!collectibles?.length) return undefined

  const fromAvailableFrom = collectibles
    .map((item) => item?.availableFrom)
    .find((value) => typeof value === 'string' && value.length > 0)

  if (typeof fromAvailableFrom === 'string') {
    const parsed = new Date(fromAvailableFrom)
    const year = parsed.getUTCFullYear()
    if (!Number.isNaN(parsed.getTime()) && Number.isFinite(year)) {
      return year
    }
  }

  const fromQuarter = collectibles
    .map((item) => item?.quarter)
    .find((value) => typeof value === 'string' && /\b(20\d{2})\b/.test(value))

  if (typeof fromQuarter === 'string') {
    const match = fromQuarter.match(/\b(20\d{2})\b/)
    const year = match?.[1] ? Number(match[1]) : NaN
    if (Number.isFinite(year)) {
      return year
    }
  }

  return undefined
}

const useMerchPage = () => {
  const pageContent = useQuery(merchPageQuery, {}, mapToMerchPageContent)
  const publicPageContent = useQuery(
    merchPageQuery,
    {},
    mapToMerchPageContent,
    {
      skipAuth: true,
      queryKeySuffix: 'public-merch-roadmap',
      enabled: !resolveRoadmapId(pageContent.data?.collectiblesRoadmap),
    },
  )
  const selectedRoadmap = pageContent.data?.collectiblesRoadmap
  const fallbackRoadmap = publicPageContent.data?.collectiblesRoadmap
  const selectedRoadmapId = resolveRoadmapId(selectedRoadmap)
  const fallbackRoadmapId = resolveRoadmapId(fallbackRoadmap)
  const collectiblesRoadmapId = selectedRoadmapId ?? fallbackRoadmapId
  const selectedRoadmapQuarterCollectibleIds =
    resolveRoadmapQuarterCollectibleIds(selectedRoadmap)
  const fallbackRoadmapQuarterCollectibleIds =
    resolveRoadmapQuarterCollectibleIds(fallbackRoadmap)
  const roadmapQuarterCollectibleIds =
    selectedRoadmapQuarterCollectibleIds.length > 0
      ? selectedRoadmapQuarterCollectibleIds
      : fallbackRoadmapQuarterCollectibleIds

  const userCollectibles = useQuery(
    userCollectiblesQuery,
    {},
    mapToUserCollectibles,
    {
      enabled: !collectiblesRoadmapId,
    },
  )
  const roadmapYear =
    resolveRoadmapYear(selectedRoadmap) ?? resolveRoadmapYear(fallbackRoadmap)
  const activeRoadmapYear = resolveYearFromUserCollectibles(
    userCollectibles.data?.collectibles,
  )
  const collectiblesYear =
    roadmapYear ??
    activeRoadmapYear ??
    new Date().getUTCFullYear()

  return {
    pageContent,
    collectiblesRoadmapId,
    collectiblesYear,
    roadmapQuarterCollectibleIds,
  }
}

export default useMerchPage
